import './clients-page.css';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import _isUndefined from 'lodash/isUndefined';

import { Button, Dropdown, Form, Input, InputNumber, Modal, Select, Space, Table, Tag, notification } from 'antd';
import { BsThreeDotsVertical } from 'react-icons/bs';
import { ExclamationCircleOutlined } from '@ant-design/icons';
import { addControlStyle } from './clients-page-style';
import { ClientForm } from './client-form';
import TextArea from "antd/es/input/TextArea";

import { Client } from '../../model/client-model';
import { DataMapSourceModel } from '../../model/data-map-source-model';

import { 
    useAddClientMutation, 
    useCopyClientMutation, 
    useDeleteClientMutation, 
    useLazyGetClientQuery, 
    useLazyGetClientsQuery, 
    useScheduleClientMutation, 
    useUnscheduleClientMutation, 
    useUpdateClientMutation 
} from '../../redux/api/clients';
import { 
    useLazyGetDataMapSourceQuery, 
    useSaveDataMapSourceMutation 
} from '../../redux/api/data-maps';
import { 
    useLazyGetDepartmentClientQuery,
    useSaveDepartmentClientMutation 
} from '../../redux/api/departments';
import { Actions, ClientActionMenu, incidentTypes, producerTypes, workflowTypes } from '../../util/constant';

const ClientsPage = () => {

    const columns = [
        {
            title: 'ID',
            dataIndex: 'id',
            key: 'id',
            width: 64,
        },
        {
            title: 'Client Code',
            dataIndex: 'clientCode',
            key: 'clientCode',
            width: 200,
            // sorter: (a: string, b: string) => a.localeCompare(b)
        },
        {
            title: 'Name',
            dataIndex: 'name',
            key: 'name',
            // sorter: (a: string, b: string) => a.localeCompare(b)
        },
        {
            title: 'Cron Schedule',
            dataIndex: 'cronSchedule',
            key: 'cronSchedule',
            width: 160,
        },
        {
            title: 'Payload',
            dataIndex: 'payloadType',
            key: 'payloadType',
            render: (payloadType: string) => payloadType ? payloadType.replace('_', ' ') : '',
            width: 104,
        },
        {
            title: 'Source Channel',
            dataIndex: 'sourceChannelType',
            key: 'sourceChannelType',
            render: (sourceChannelType: string) => sourceChannelType ? sourceChannelType.replace('_', ' ') : '',
            width: 144,
        },
        {
            title: 'Destination Channel',
            dataIndex: 'destinationChannelType',
            key: 'destinationChannelType',
            render: (destinationChannelType: string) => destinationChannelType ? destinationChannelType.replace('_', ' ') : '',
            width: 176,
        },
        {
            title: 'Status',
            dataIndex: 'scheduled',
            key: 'scheduled',
            width: 120,
            render: (scheduled: boolean) => {
                return scheduled ? <Tag color="green">Scheduled</Tag> : <Tag color="red">Unscheduled</Tag>
            }
        },
        {
            title: 'Action',
            dataIndex: 'action',
            key: 'action',
            width: 96,
            render: (_: any, record: Client) => {
                return <Dropdown
                    trigger={['click']}
                    menu={{
                        items: getMenuItems(record.cronSchedule, record.scheduled, record.payloadType),
                        onClick: ({ key }) => handleClientAction(key, record)
                    }}>
                    <div style={{ cursor: 'pointer' }} onClick={(e) => e.preventDefault()}>
                        <Space>
                            <span className="dropdown-title">
                                <BsThreeDotsVertical />
                            </span>
                        </Space>
                    </div>
                </Dropdown>
            }
        },
    ];

    const [ isAddOpen, setAddOpen ] = useState<boolean>(false);
    const [ isEditOpen, setEditOpen ] = useState<boolean>(false);
    const [ isSourceDataOpen, setSourceDataOpen ] = useState<boolean>();
    const [ isDepartmentMappingOpen, setDepartmentMappingOpen ] = useState<boolean>();
    const [ isScheduleOpen, setScheduleOpen ] = useState<boolean>(false);
    const [ isUnscheduleOpen, setUnscheduleOpen ] = useState<boolean>(false);
    const [ isCopyOpen, setCopyOpen ] = useState<boolean>(false);
    const [ clients, setClients ] = useState<Client[]>([]);
    const [ tableHeight, setTableHeight ] = useState(0);
    const [ page, setPage ] = useState<any>({
        current: 1,
        pageSize: 25,
        total: 0
    });

    const [ client, setClient] = useState<Client>();

    const [ modal, contextHolder] = Modal.useModal();

    const [ getClient ] = useLazyGetClientQuery();
    const [ getClients ] = useLazyGetClientsQuery();
    const [ postClient ] = useAddClientMutation();
    const [ getDepartmentClient ] = useLazyGetDepartmentClientQuery();
    const [ postDepartmentClient ] = useSaveDepartmentClientMutation();
    const [ putClient ] = useUpdateClientMutation();
    const [ removeClient ] = useDeleteClientMutation();
    const [ postCopyClient ] = useCopyClientMutation();
    const [ postScheduleClient ] = useScheduleClientMutation();
    const [ postUnscheduleClient ] = useUnscheduleClientMutation();
    const [ getDataMapSource ] = useLazyGetDataMapSourceQuery();
    const [ saveDataMapSource ] = useSaveDataMapSourceMutation();
    
    const navigate = useNavigate();

    const [newClientForm] = Form.useForm();
    const [departmentForm] = Form.useForm();
    const [sourceDataForm] = Form.useForm();

    const updateTableHeight = () => {
        const windowHeight = window.innerHeight; // Full screen height
        const calculatedHeight = windowHeight - 288; // Subtract paddings and headers
        setTableHeight(calculatedHeight);
    };
    
    useEffect(() => {
        updateTableHeight(); // Set height on mount
        window.addEventListener("resize", updateTableHeight); // Adjust height on resize
        return () => window.removeEventListener("resize", updateTableHeight);
    }, []);

    useEffect(() => {
        fetchClients();
    }, [page.current, getClients]);

    const fetchClients = async () => {
        const clients = await getClients({index: page.current, viewableSize: page.pageSize}).unwrap();
        const indexedClients = clients.items.map((client: Client, index: number) => {
            return { ...client, key: index };
        });
        setClients(indexedClients);
        setPage({...page, viewableSize: clients.page.viewableSize, total: clients.page.totalSize});
    }

    /**
     * Gets the menu items available to the client depending on its current state
     * 
     * @param scheduled if scheduled
     * @param payloadType the payload type (XML, JSON, PDF, etc)
     * @returns the menu items
     */
    const getMenuItems = (cronSchedule: string, scheduled: boolean | undefined, payloadType: string | undefined ): any[] => {
        return [
            ClientActionMenu.updateMenu,
            ClientActionMenu.deleteMenu,
            ClientActionMenu.copyMenu,
            {type: 'divider'},
            ClientActionMenu.sourceDataMenu,
            payloadType === 'PDF' ? ClientActionMenu.pdfBuilderMenu : ClientActionMenu.dataMappingMenu,
            ClientActionMenu.scriptEditorMenu,
            {type: 'divider'},
            cronSchedule ? (!scheduled ? ClientActionMenu.scheduleMenu : ClientActionMenu.unscheduleMenu) : null,
            {type: 'divider'},
            ClientActionMenu.departmentMenu,
        ];
    }

    /**
     * Opens the Add Client Modal.
     */
    const openAddModal = (): void => {
        setAddOpen(true);
    }

    /**
     * Adds a client by sending the client data to the server.
     * 
     * @param client the client to add
     */
    const addClient = async (client: Client): Promise<void> => {

        try {

            const sourceData = client.sourceData;
            delete client.sourceData;
            const response = await postClient(client).unwrap();
            
            let newClients: Client[] = [...clients, response];
            setClients(newClients);
            
            await saveDataMapSource({clientId: response.id, source: sourceData});
            
            setAddOpen(false);

            notification.success({
                message: "Client Created!",
                description: `The client ${response.name} has been created.`
            });
        } catch (e) {
            console.log(e);
            notification.error({
                message: "Creating Client Failed!",
                description: `An error occurred while creating the client`
            });
        }
    }

    const updateClient = async (client: Client): Promise<void> => {

        try {

            const sourceData = client.sourceData;
            delete client.sourceData;
            await putClient({id: client.id, payload: client}).unwrap();
            fetchClients();

            await saveDataMapSource({clientId: client.id, source: sourceData});
            
            setEditOpen(false);

            notification.success({
                message: "Client Update!",
                description: `The client ${client.name} has been update.`
            });
        } catch (e) {
            console.log(e);
            notification.error({
                message: "Updating Client Failed!",
                description: `An error occurred while creating the client`
            });
        }
    }

    const deleteClient = async (client: Client): Promise<void> => {
        try {
            await removeClient(client).unwrap();
            const temp = [...clients];
            
            let ndx = temp.findIndex(c => c.id === client.id);
            temp.splice(ndx, 1);
            let newClients: Client[] = [...temp];
            setClients(newClients);

            notification.success({
                message: "Client Deleted!",
                description: `The client ${client.name} has been deleted.`
            });

        } catch (e) {
            notification.error({
                message: "Deleting Client Failed!",
                description: `An error occurred while deleting the client.`
            });
        }
    }

    /**
     * Opens the source data modal.
     */
    const openSourceData = async (client: Client) => {
        
        const dataMapSource: DataMapSourceModel = await getDataMapSource(client.id).unwrap();
        
        sourceDataForm.setFieldValue('clientId',client.id);
        sourceDataForm.setFieldValue('source', dataMapSource?.source);
        
        setSourceDataOpen(true);
    }

    /**
     * Opens the script editor page.
     */
    const openScriptEditorPage = (client: Client) => {
        navigate(`/script-editor/${client?.id}`);
    }

    /**
     * Opens the script editor page.
     */
    const openDepartmentMapping = async (client: Client) => {
        
        try {
            const departmentClient = await getDepartmentClient(client.id).unwrap();
            departmentForm.setFieldValue('departmentId', departmentClient.departmentId);
            departmentForm.setFieldValue('workflowType', departmentClient.workflowType);
            departmentForm.setFieldValue('producerType', departmentClient.producerType);
            departmentForm.setFieldValue('incidentType', departmentClient.incidentType);
            setDepartmentMappingOpen(true);
        } catch (e) {
            console.log(e);
        }
    }

    /**
     * Opens the client's data mapping page.
     */
    const openDataMappingPage = (client: Client) => {
        navigate(`/data-mapping/${client?.id}`);
    }
    
    /**
     * Opens the pdf bulder page.
     */
    const openPdfBuilderPage = (client: Client) => {
        navigate(`/pdf-builder/${client?.id}`);
    }

    const mapDepartment = async (value: any) => {
        await postDepartmentClient({clientId: client?.id, payload: value}).unwrap();
        setDepartmentMappingOpen(false);
    }

    const saveSourceData = async(value: any) => {
        await saveDataMapSource({clientId: value.clientId, source: value.source});
        setSourceDataOpen(false);
    }

    /**
     * Schedules a client for fetching data and sending to target client.
     */
    const scheduleClient = async () => {

        try {
            await postScheduleClient(client?.id).unwrap();
            fetchClients();
            
            notification.success({
                message: "Client Scheduled!",
                description: `The client ${client?.name} has been scheduled for dispatching.`
            });
        } catch (e: any) {
            notification.error({
                message: "Scheduling Client Failed!",
                description: e.data ? e.data : `An error occurred while scheduling the client`
            });
        }
        setScheduleOpen(false);
    }

    /**
     * Cancels the scheduled client from fetching data and sending to target client.
     */
    const unscheduleClient = async () => {

        try {
            await postUnscheduleClient(client?.id).unwrap();
            fetchClients();
            
            notification.success({
                message: "Client Schedule Canceled!",
                description: `The schedule of client ${client?.name} has been canceled.`
            });
        } catch (e) {
            notification.error({
                message: "Cancel Client Schedule Failed!",
                description: `An error occurred while cancelling the client's schedule. ${e}`
            });
        }

        setUnscheduleOpen(false);
    }

    const copyClient = async (value: any) => {

        try {   
            await postCopyClient({
                clientId: client?.id, 
                clientCode: value.clientCode, 
                name: value.name
            }).unwrap();
            fetchClients();

            setCopyOpen(false);
            notification.success({
                message: "Client Copie!",
                description: `The operation to copy a client ${client?.name} is successful.`
            });
          } catch (e) {
            console.log(e);
            notification.error({
                message: "Cancel Client Schedule Failed!",
                description: `An error occurred while copying the client. ${e}`
            });
        }
    }

    /**
     * Cancels the schedule client confirmation modal.
     */
    const closeScheduleModal = (): void => {
        setScheduleOpen(false);
    }

    /**
     * Cancels the client schedule canceling confirmation modal.
     */
    const closeUnscheduleModal = (): void => {
        setUnscheduleOpen(false);
    }

    /**
     * Cancels the create client copy modal.
     */
    const closeCopyModal = (): void => {
        setCopyOpen(false);
    }

    const onEditOpen = async (dto: Client) => {
        const client: Client = await getClient(dto.id).unwrap();
        const source: DataMapSourceModel = await getDataMapSource(client.id).unwrap();
        const clientWithSourceData = {...client, sourceData: source?.source};
        setClient(clientWithSourceData);
        setEditOpen(true);
    }

    const onDelete = (client: Client) => {
      
        modal.confirm({
            title: 'Delete the client',
            icon: <ExclamationCircleOutlined />,
            content: 'Are you sure to delete this client?',
            okText: 'Ok',
            cancelText: 'Cancel',
            onOk: () => deleteClient(client),
        });
    }

    /**
     * Handles the click action on the action button of the client
     * in the client list (the 3-dot vertical button on the right-most
     * column)
     * 
     * @param key the key of the menu item that was clicked
     * @param client the client that was clicked
     */
    const handleClientAction = (key: string, client: Client) => {
        
        setClient(client);
        
        switch (key) {
            case Actions.UPDATE: onEditOpen(client); break;
            case Actions.DELETE: onDelete(client); break;
            case Actions.SOURCE: openSourceData(client); break;
            case Actions.SCRIPT: openScriptEditorPage(client); break;
            case Actions.DATA_MAPPING: openDataMappingPage(client); break;
            case Actions.DEPARTMENT: openDepartmentMapping(client); break;
            case Actions.PDF_BUILDER: openPdfBuilderPage(client); break;
            case Actions.SCHEDULE: setScheduleOpen(true); break;
            case Actions.UNSCHEDULE: setUnscheduleOpen(true); break;
            case Actions.COPY: setCopyOpen(true); break;
        }
    }

    // Handle page change
    const handleTableChange = (page: any) => {
      setPage((prev: any) => ({ ...prev, current: page.current }));
    };

    return (
        <>
            {contextHolder}
            <div style={addControlStyle}>
                <h1>Clients</h1>
                <Button
                    type="primary"
                    onClick={openAddModal}
                >
                    Create Client
                </Button>
            </div>

            <Table 
                dataSource={clients} 
                columns={columns} 
                scroll={{ y: tableHeight }}
                pagination={{ 
                    current: page.current, 
                    pageSize: page.pageSize, 
                    total: page.total 
                }}
                onChange={handleTableChange}/>
            
            <Modal
                closable={false}
                open={isAddOpen}
                width={600}
                footer={null}
                destroyOnClose={true}
            >
                <ClientForm 
                    onClose={() => setAddOpen(false)}
                    addClient={addClient}
                />
            </Modal>

            <Modal
                title={'Client Information'}
                closable={false}
                open={isEditOpen}
                width={600}
                footer={null}
                destroyOnClose={true}
            >
                <ClientForm 
                    client={client}
                    onClose={() => setEditOpen(false)}
                    updateClient={updateClient}
                />
            </Modal>

            <Modal
                title={'Set Source Data'}
                closable={false}
                open={isSourceDataOpen}
                onOk={() => sourceDataForm.submit()}
                onCancel={() => setSourceDataOpen(false)}
                width={600}
                destroyOnClose={true}
            >
                <Form 
                    form={sourceDataForm}
                    onFinish={saveSourceData}
                    >
                    <Form.Item name="clientId">
                        <Input style={{display: 'none'}} />
                    </Form.Item>

                    <label>Source Data *</label>
                    <Form.Item
                        name="source"
                        rules={[{required: true, message: 'Source Data is is required.'}]}
                    >
                        <TextArea style={{width: '100%', height: 600}} />
                    </Form.Item>
                </Form>
            </Modal>

            <Modal
                title={'Set Department Mapping'}
                closable={false}
                open={isDepartmentMappingOpen}
                onOk={() => departmentForm.submit()}
                onCancel={() => setDepartmentMappingOpen(false)}
                width={400}
                destroyOnClose={true}
            >
                <Form 
                    form={departmentForm}
                    onFinish={mapDepartment}
                    >
                    <label>Department ID *</label>
                    <Form.Item
                        name="departmentId"
                        rules={[{required: true, message: 'Department ID is required.'}]}
                    >
                        <InputNumber style={{width: '100%'}} />
                    </Form.Item>
                    <label>Workflow Type *</label>
                    <Form.Item
                        name="workflowType"
                        rules={[{required: true, message: 'Workflow Type is required.'}]}
                    >
                        <Select options={workflowTypes} />
                    </Form.Item>
                    <label>Producer Type *</label>
                    <Form.Item
                        name="producerType"
                        rules={[{required: true, message: 'Producer Type is required.'}]}
                    >
                        <Select options={producerTypes} />
                    </Form.Item>
                    <label>Incident Type *</label>
                    <Form.Item
                        name="incidentType"
                        rules={[{required: true, message: 'Incident Type is required.'}]}
                    >
                        <Select options={incidentTypes} />
                    </Form.Item>
                </Form>
            </Modal>

            <Modal
                title={'Schedule Client'}
                closable={false}
                open={isScheduleOpen}
                onOk={scheduleClient}
                onCancel={closeScheduleModal}
                width={600}
            >
                <p>Are you sure you want to schedule the client <span style={{ color: 'green' }}>[{client?.name}]</span> for dispatching?</p>
            </Modal>

            <Modal
                title={'Cancel Client Schedule'}
                closable={false}
                open={isUnscheduleOpen}
                onOk={unscheduleClient}
                onCancel={closeUnscheduleModal}
                width={600}
            >
                <p>Are you sure you want to cancel the schedule for client <span style={{ color: 'green' }}>[{client?.name}]</span>?</p>
            </Modal>

            <Modal
                title={'Create Copy'}
                closable={false}
                open={isCopyOpen}
                onOk={() => newClientForm.submit()}
                onCancel={closeCopyModal}
                width={400}
                destroyOnClose={true}
            >
                <Form 
                    form={newClientForm}
                    onFinish={copyClient}
                    >
                    <label>Name *</label>
                    <Form.Item
                        name="name"
                        rules={[{required: true, message: 'Name is required.'}]}
                    >
                        <Input />
                    </Form.Item>
                
                    <label>Client Code *</label>
                    <Form.Item
                        name="clientCode"
                        rules={[{required: true, message: 'Client code is required.'}]}
                    >
                        <Input />
                    </Form.Item>
                </Form>
            </Modal>
        </>
    );
};
export default ClientsPage;